import { ImageProps } from 'next/image';
import { HTMLAttributeAnchorTarget, MouseEvent, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { ampli } from '../../../core/Tracking/Amplitude/__AUTO_GENERATED__';
import {
    TrackingProperties,
    useTrackEventAmplitude,
} from '../../../core/Tracking/Amplitude/Amplitude';
import { DestinationCard as DestinationCardFromDesignSystem } from '../../../design-system/components/card/destination/DestinationCard';
import { ReviewRatingProps } from '../../../design-system/components/review/rating/ReviewRating';
import { ReviewRatingFallback } from '../ReviewRating/ReviewRatingFallback';

type Props = {
    href: string;
    image: ImageProps;
    target?: HTMLAttributeAnchorTarget;
    title: string;
    small?: boolean;
    showReviewRating?: boolean;
    reviewRatingProps?: Pick<
        ReviewRatingProps,
        'ratingValue' | 'maxRating' | 'minRating' | 'reviewsCount'
    >;
    trackingInformation?: {
        click: TrackingProperties<typeof ampli.destinationClicked>;
    };
    onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
};

export function DestinationCard({ onClick, trackingInformation, ...props }: Props) {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();

    let reviewRatingProps: ReviewRatingProps | undefined;
    if (props.reviewRatingProps) {
        const ariaLabel: ReviewRatingProps['ariaLabel'] = (rating) => {
            return formatMessage(
                { id: 'global.component.reviewRating.iconsDescription' },
                {
                    rating: rating.value,
                    maxRating: rating.max,
                },
            );
        };

        reviewRatingProps = {
            ratingValue: props.reviewRatingProps.ratingValue,
            maxRating: props.reviewRatingProps.maxRating,
            minRating: props.reviewRatingProps.minRating,
            ariaLabel,
            reviewsCount: formatMessage(
                { id: 'global.component.reviewRating.reviewsCount' },
                { reviewsCount: props.reviewRatingProps.reviewsCount },
            ),
            fallback: (error) => <ReviewRatingFallback error={error} />,
            showRatingNumber: true,
            size: 'small',
        };
    }

    const handleClick = useCallback(
        (event: MouseEvent<HTMLAnchorElement>) => {
            if (typeof onClick === 'function') {
                onClick(event);
            }

            if (trackingInformation?.click) {
                trackEvent((ampli, defaultProperties) => {
                    ampli.destinationClicked({
                        ...defaultProperties,
                        ...trackingInformation.click,
                    });
                });
            }
        },
        [onClick, trackEvent, trackingInformation],
    );

    return (
        <DestinationCardFromDesignSystem
            {...props}
            reviewRatingProps={reviewRatingProps}
            onClick={handleClick}
        />
    );
}

DestinationCard.PICTURE_RATIO = DestinationCardFromDesignSystem.PICTURE_RATIO;
